import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Users, ArrowRight, Wallet, HandCoins, Eye, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { DemoBanner, Logo } from "@/components/Layout";
import { Field, useLoad } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function Landing() {
  const { user } = useAuth();
  const steps = [["1", "Sign up", "Fill in a short form with your name, phone and NRC. It takes 2 minutes."], ["2", "Send money by MTN or Airtel", "Pay to our official company number. We confirm it and you see it in your account."], ["3", "Watch it grow", "Earn from loans, save with your village group, or borrow when you need it."]];
  const ways = [[Wallet, "Invest", "Put your money to work. It is lent to checked borrowers and you earn a share of the interest they pay.", "apply"], [Users, "Village banking", "Save a set amount each month with people you trust, borrow from the group, and share the profit at year end.", "apply"], [HandCoins, "Borrow", "Get a small loan with clear costs shown up front. Pay back on time to unlock bigger loans.", "apply"]];
  return (
    <div className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-[#050b18] text-white">
        <img src="/hero1.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050b18] via-[#050b18]/85 to-transparent" />
        <header className="relative mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <Logo light />
          <div className="flex gap-2">
            {user ? <Button asChild className="rounded-full bg-[#0b63f6]" data-testid="go-dashboard"><Link to="/app">Open my account</Link></Button> : (
              <>
                <Button variant="ghost" asChild className="hidden text-white hover:bg-white/10 hover:text-white sm:inline-flex" data-testid="landing-guides"><Link to="/guides">Guides</Link></Button>
                <Button variant="ghost" asChild className="text-white hover:bg-white/10 hover:text-white" data-testid="landing-login"><Link to="/login">Sign in</Link></Button>
                <Button asChild className="rounded-full bg-[#0b63f6] hover:bg-[#0a54d1]" data-testid="landing-apply"><Link to="/apply">Join now</Link></Button>
              </>
            )}
          </div>
        </header>
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-20 pt-10 lg:grid-cols-[1.3fr_1fr] lg:pb-28 lg:pt-16">
          <div className="fade-up">
            <div className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-sky-200 ring-1 ring-white/20">Made for Zambia · Kwacha · MTN & Airtel</div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">Save, invest and borrow — <span className="text-[#4d8dff]">together.</span></h1>
            <p className="mt-6 max-w-xl text-base text-sky-50/90 sm:text-lg">Tiyeni Capital helps families, friends and village groups grow their money. Every kwacha you send is checked and shown clearly in your account, so you always know where your money is.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-[#0b63f6] px-7 hover:bg-[#0a54d1]" data-testid="hero-apply"><Link to="/apply">Create my account <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white" data-testid="hero-login"><Link to="/login">Sign in</Link></Button>
            </div>
          </div>
          <div className="hidden justify-center lg:flex fade-up"><img src="/logo.png" alt="Tiyeni Capital logo" className="w-80 rounded-3xl shadow-2xl shadow-black/60 ring-1 ring-white/10" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="overline mb-3">What you can do</div>
        <h2 className="font-heading text-3xl font-bold text-brand">Three simple ways to grow</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3 stagger">
          {ways.map(([I, t, d]) => (
            <Link to="/apply" key={t} className="group rounded-2xl border border-stone-200 bg-white p-7 lift" data-testid={`way-${t.toLowerCase().replace(/ /g, "-")}`}>
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#0b63f6]/10"><I className="h-6 w-6 text-[#0b63f6]" /></div>
              <h3 className="mt-5 font-heading text-xl font-semibold text-brand">{t}</h3><p className="mt-2 text-sm leading-relaxed text-stone-600">{d}</p>
              <div className="mt-5 inline-flex items-center text-sm font-semibold text-[#0b63f6]">Get started <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
            </Link>
          ))}
        </div>
        <div className="mt-10 flex flex-col items-start justify-between gap-4 rounded-2xl border border-sky-200 bg-sky-50 p-6 sm:flex-row sm:items-center" data-testid="landing-guides-box">
          <div><h3 className="font-heading text-lg font-semibold text-brand">Free guides with examples</h3><p className="text-sm text-stone-600">Investing, village banking, borrowing and becoming a credit agent — download as PDF or PowerPoint.</p></div>
          <Button asChild className="rounded-full bg-[#0b63f6]" data-testid="landing-guides-open"><Link to="/guides">See the guides</Link></Button>
        </div>
      </section>

      <section className="bg-sky-50/70">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 lg:grid-cols-2">
          <img src="/hero2.jpg" alt="Village savings group members" className="h-80 w-full rounded-3xl object-cover shadow-lg lg:h-[26rem]" />
          <div>
            <div className="overline mb-3">How it works</div>
            <h2 className="font-heading text-3xl font-bold text-brand">Start in three steps</h2>
            <ol className="mt-8 space-y-6">{steps.map(([n, t, d]) => (
              <li key={n} className="flex gap-4"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#0b63f6] font-heading font-bold text-white">{n}</span><div><div className="font-heading text-lg font-semibold text-brand">{t}</div><p className="text-sm text-stone-600">{d}</p></div></li>))}
            </ol>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid gap-5 md:grid-cols-3">
          {[[ShieldCheck, "Your money is protected", "For every K1 investors put in, the company keeps K1 of its own money aside as a safety net."], [Eye, "Nothing hidden", "You see every deposit, loan, fee and payout in your account — in plain words."], [Smartphone, "Pay the way you already do", "Use MTN or Airtel mobile money. No bank account needed."]].map(([I, t, d]) => (
            <div key={t} className="rounded-2xl bg-[#050b18] p-7 text-white"><I className="h-7 w-7 text-[#4d8dff]" /><h3 className="mt-4 font-heading text-lg font-semibold">{t}</h3><p className="mt-2 text-sm text-sky-100/80">{d}</p></div>))}
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-3xl bg-[#0b63f6] p-8 text-white sm:flex-row sm:items-center sm:p-10">
          <div><h2 className="font-heading text-2xl font-bold sm:text-3xl">Ready to grow with us?</h2><p className="mt-1 text-sky-100">Join today — it only takes 2 minutes.</p></div>
          <Button asChild size="lg" className="rounded-full bg-white px-8 text-[#0b63f6] hover:bg-sky-50" data-testid="cta-apply"><Link to="/apply">Create my account</Link></Button>
        </div>
      </section>
      <footer className="border-t py-8 text-center text-xs text-stone-500">© {new Date().getFullYear()} Tiyeni Capital. Group savings and investor protection are company rules, not government insurance.</footer>
    </div>
  );
}

function AuthShell({ title, sub, children, aside }) {
  return (
    <div className="min-h-screen">
      <DemoBanner />
      <div className={`mx-auto px-5 py-10 ${aside ? "max-w-5xl" : "max-w-md"}`}>
        <Logo />
        <div className={`mt-8 grid items-start gap-6 ${aside ? "lg:grid-cols-[minmax(0,26rem)_1fr]" : ""}`}>
          <div className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-8 shadow-sm fade-up">
            <h1 className="font-heading text-2xl font-bold text-brand">{title}</h1>
            {sub && <p className="mt-1 text-sm text-stone-600">{sub}</p>}
            <div className="mt-6">{children}</div>
          </div>
          {aside}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [step, setStep] = useState("creds");
  const [pw, setPw] = useState({ token: "", name: "", a: "", b: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const next = sp.get("next") || "/app";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: f.email, password: f.password });
      if (data.password_setup_required) { setPw({ token: data.setup_token, name: data.name, a: "", b: "" }); setStep("newpw"); return; }
      await finish(data);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  const finish = async (data) => {
    localStorage.setItem("tiyeni_token", data.token);
    await signIn(data.token);
    nav(next);
  };
  const savePw = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.a !== pw.b) return setErr("The two passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { setup_token: pw.token, new_password: pw.a });
      await finish(data);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  if (step === "newpw") return (
    <AuthShell title={`Welcome, ${pw.name.split(" ")[0]}`} sub="This is your first sign-in. Choose your own password now.">
      <form onSubmit={savePw} className="space-y-4" data-testid="set-password-form">
        <Field label="New password" hint="At least 8 characters, with letters and numbers"><Input type="password" autoComplete="new-password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} required minLength={8} data-testid="set-pw-new" /></Field>
        <Field label="Type it again"><Input type="password" autoComplete="new-password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} required data-testid="set-pw-confirm" /></Field>
        {err && <p className="text-sm text-rose-700" data-testid="login-error">{err}</p>}
        <Button className="w-full bg-[#0369a1]" disabled={busy} data-testid="set-pw-submit">Save password and continue</Button>
      </form>
    </AuthShell>
  );
  return (
    <AuthShell title="Sign in" sub="Welcome back.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email"><Input type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="login-email" /></Field>
        <Field label="Password"><Input type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="login-password" /></Field>
        {err && <p className="text-sm text-rose-700" data-testid="login-error">{err}</p>}
        <Button className="w-full bg-[#0369a1]" disabled={busy} data-testid="login-submit">Sign in</Button>
      </form>
      <p className="mt-6 text-center text-sm text-stone-600">New here? <Link className="font-semibold text-brand underline" to={`/apply${sp.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid="login-to-apply">Apply for an account</Link></p>
      <p className="mt-4 text-center text-xs text-stone-500">Added by an admin? Sign in the first time with your email and any password — we will ask you to choose your own.</p>
    </AuthShell>
  );
}

export function Apply() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const ref = sp.get("ref") || localStorage.getItem("tiyeni_ref") || "";
  const { data: agent } = useLoad(ref ? `/referrals/code/${ref}` : null);
  if (sp.get("ref")) localStorage.setItem("tiyeni_ref", sp.get("ref"));
  const [f, setF] = useState({ full_name: "", phone: "+260", address: "", account_type: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!/^\+260\d{9}$/.test(f.phone.replace(/\s/g, ""))) return setErr("Phone must look like +260971234567");
    if (!f.account_type) return setErr("Choose what kind of account you want");
    try {
      const { data } = await api.post("/applications", { ...f, phone: f.phone.replace(/\s/g, ""), ref });
      await signIn(data.token);
      nav(sp.get("next") || "/app");
    } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <AuthShell title="Apply to join" sub="Takes about 2 minutes. We'll review and let you know.">
      {agent?.valid && <div className="mb-4 rounded-lg bg-sky-50 p-3 text-sm text-sky-900" data-testid="apply-invited-by">You were invited by <b>{agent.agent_name}</b>. They can help you get started.</div>}
      <form onSubmit={submit} className="space-y-4" data-testid="application-form">
        <Field label="Full names"><Input value={f.full_name} onChange={set("full_name")} required data-testid="apply-full-name" /></Field>
        <Field label="Phone number" hint="Zambian format, e.g. +260971234567"><Input value={f.phone} onChange={set("phone")} required data-testid="apply-phone" /></Field>
        <Field label="Home address"><Input value={f.address} onChange={set("address")} required data-testid="apply-address" /></Field>
        <Field label="I want to">
          <Select value={f.account_type} onValueChange={(v) => setF({ ...f, account_type: v })}>
            <SelectTrigger data-testid="apply-account-type"><SelectValue placeholder="Choose account type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="investor" data-testid="apply-type-investor">Invest / save (investor)</SelectItem>
              <SelectItem value="borrower" data-testid="apply-type-borrower">Borrow (borrower)</SelectItem>
              <SelectItem value="both" data-testid="apply-type-both">Both</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} required data-testid="apply-email" /></Field>
        <Field label="Create a password" hint="At least 8 characters"><Input type="password" value={f.password} onChange={set("password")} required minLength={8} data-testid="apply-password" /></Field>
        {err && <p className="text-sm text-rose-700" data-testid="apply-error">{err}</p>}
        <Button className="w-full bg-[#0369a1]" data-testid="apply-submit">Submit application</Button>
        <p className="text-xs text-stone-500">We keep your details private. Only authorised staff can see them.</p>
      </form>
    </AuthShell>
  );
}
